import { Button, makeStyles, mergeClasses, ToggleButton, tokens, Tooltip } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import {
    AddSquare20Filled,
    AddSquare20Regular,
    bundleIcon,
    ChevronDoubleLeftRegular,
    ChevronDoubleRightRegular,
    Pin20Filled,
    Pin20Regular,
    SubtractSquare20Filled,
    SubtractSquare20Regular,
} from '@fluentui/react-icons';
import { SidebarComponent } from '@syncfusion/ej2-react-navigations';
import { cloneDeep } from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormView } from '../../../contexts/useFormView';
import { TaxReturnTOCItem } from '../../../model';
import { customPalette } from '../../../theme';
import { filterTOCItemsByVisibility, getFilteredTOCItems } from '../../../utils/TaxReturnTOCUtils';
import FormTOCControlledTreeView from './FormTOCControlledTreeView';
import FormTOCSidebarSearch from './FormTOCSidebarSearch';

export const TOC_SIDEBAR_WIDTH = 400;
export const CLOSED_TOC_SIDEBAR_WIDTH = 50;

const PinIconBundle = bundleIcon(Pin20Filled, Pin20Regular);
const AddSquareIconBundle = bundleIcon(AddSquare20Filled, AddSquare20Regular);
const SubtractSquareIconBundle = bundleIcon(SubtractSquare20Filled, SubtractSquare20Regular);

const useStyles = makeStyles({
    focusable: {
        ':focus': {
            border: '2px solid black',
            borderRadius: '5px',
        },
    },
    expandedHeaderContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    expandedHeaderContainer: {
        position: 'sticky',
        top: 0,
        zIndex: 5,
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'column',
    },
    collapsedContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    title: {
        marginLeft: '20px',
        textAlign: 'center',
    },
    disabledContent: {
        opacity: 0.55,
        color: tokens.colorNeutralForegroundDisabled,
    },
    collapsedButton: {
        width: '40px',
        height: '50px',
        marginBottom: '10px',
        color: customPalette.themePrimary,
        ':focus': {
            border: '2px solid black',
            borderRadius: '5px',
        },
    },
    formsText: {
        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        padding: '0px',
        textAlign: 'center',
        color: customPalette.neutralDark,
        fontSize: '12px',
        fontWeight: '600',
    },
});

const FormTOCSidebar: React.FC = () => {
    const styles = useStyles();
    const [isDrawerExpanded, { setFalse: hideDrawer, setTrue: showDrawer }] = useBoolean(true);
    const { t } = useTranslation();
    const {
        isLoading,
        isDrawerPinned,
        togglePinDrawer,
        expandAllTocNodes,
        collapseAllTocNodes,
        tocData,
        tocTreeState,
        setTocSearchText,
        currentTOCItemState,
    } = useFormView();
    const tocItems: TaxReturnTOCItem[] = tocData?.items ?? [];
    const searchText: string = tocTreeState.searchText;
    const filteredItemsRaw: TaxReturnTOCItem[] = searchText.length > 0 ? getFilteredTOCItems(cloneDeep(tocItems), searchText) : tocItems;
    const filteredItems: TaxReturnTOCItem[] = filterTOCItemsByVisibility(filteredItemsRaw);

    const collapseDrawerAndClearSearch = () => {
        // Clear reducer state only — navigating here races with Form/Report tab switches
        // when Syncfusion fires close on document click.
        setTocSearchText('', { syncUrl: false });
        hideDrawer();
    };

    const handleToggleDrawer = () => {
        if (isDrawerExpanded) {
            collapseDrawerAndClearSearch();
            return;
        }

        showDrawer();
    };

    const handleEscPress = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isDrawerExpanded && !isDrawerPinned) {
            collapseDrawerAndClearSearch();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleEscPress);
        return () => {
            window.removeEventListener('keydown', handleEscPress);
        };
    }, [isDrawerExpanded, isDrawerPinned]);

    const toggleDrawerAriaLabel = (isDrawerExpanded ? t('collapseToc') : t('expandToc')).toString();
    const togglePinDrawerAriaLabel = (isDrawerPinned ? t('unpinToc') : t('pinToc')).toString();

    const expandAll = () => {
        expandAllTocNodes();
    };

    const collapseAll = () => {
        collapseAllTocNodes();
    };

    return (
        <SidebarComponent
            id='dockSidebar'
            data-testid='dockSidebar'
            animate={false}
            enableDock={true}
            closeOnDocumentClick={!isDrawerPinned}
            style={{ backgroundColor: tokens.colorNeutralBackground1 }}
            dockSize={CLOSED_TOC_SIDEBAR_WIDTH}
            width={TOC_SIDEBAR_WIDTH}
            close={collapseDrawerAndClearSearch}
            open={showDrawer}
            isOpen={isDrawerExpanded}
            target='#form-viewer-container'
            type='Over'
            position='Right'
            zIndex={200}
        >
            {isDrawerExpanded ? (
                <div style={{ width: '100%' }}>
                    <div className={styles.expandedHeaderContainer}>
                        <div className={styles.expandedHeaderContent}>
                            <h3 className={mergeClasses(styles.title, isLoading && styles.disabledContent)}>{t('formsAndReports')}</h3>
                            <div>
                                <span className={isLoading ? styles.disabledContent : undefined}>
                                    <Tooltip
                                        content={t('expandAllForms').toString()}
                                        relationship='description'
                                        appearance='inverted'
                                        withArrow
                                        positioning='above'
                                    >
                                        <Button
                                            appearance='subtle'
                                            icon={<AddSquareIconBundle />}
                                            aria-label={t('expandAllForms').toString()}
                                            className={styles.focusable}
                                            onClick={expandAll}
                                            data-testid='expand-folder-button'
                                            disabled={isLoading}
                                        />
                                    </Tooltip>
                                    <Tooltip
                                        content={t('collapseAllForms').toString()}
                                        relationship='description'
                                        appearance='inverted'
                                        withArrow
                                        positioning='above'
                                    >
                                        <Button
                                            appearance='subtle'
                                            icon={<SubtractSquareIconBundle />}
                                            aria-label={t('collapseAllForms').toString()}
                                            className={styles.focusable}
                                            onClick={collapseAll}
                                            data-testid='collapse-folder-button'
                                            disabled={isLoading}
                                        />
                                    </Tooltip>
                                </span>

                                <Tooltip
                                    content={togglePinDrawerAriaLabel}
                                    relationship='description'
                                    appearance='inverted'
                                    withArrow
                                    positioning='above'
                                >
                                    <ToggleButton
                                        appearance='subtle'
                                        checked={isDrawerPinned}
                                        icon={<PinIconBundle />}
                                        aria-label={togglePinDrawerAriaLabel}
                                        className={styles.focusable}
                                        onClick={togglePinDrawer}
                                        data-testid='pin-button'
                                    />
                                </Tooltip>

                                <Tooltip
                                    content={toggleDrawerAriaLabel}
                                    relationship='description'
                                    appearance='inverted'
                                    withArrow
                                    positioning='above'
                                >
                                    <ToggleButton
                                        appearance='subtle'
                                        checked={isDrawerExpanded}
                                        icon={isDrawerExpanded ? <ChevronDoubleRightRegular /> : <ChevronDoubleLeftRegular />}
                                        aria-label={toggleDrawerAriaLabel}
                                        className={styles.focusable}
                                        onClick={handleToggleDrawer}
                                        data-testid='collapse-button'
                                        disabled={isDrawerPinned}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                        <div className={isLoading ? styles.disabledContent : undefined}>
                            <FormTOCSidebarSearch
                                searchText={tocTreeState.searchText}
                                onSearchTextChange={setTocSearchText}
                                total={filteredItems.length}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <FormTOCControlledTreeView
                        filteredItems={filteredItems}
                        noRecordsMessage={t('noRecordsToDisplay')}
                        selectedNodeId={currentTOCItemState?.id}
                        selectedFormPageName={currentTOCItemState?.tocKey?.formPageName ?? currentTOCItemState?.tocItemTitle}
                    />
                </div>
            ) : (
                <div className={styles.collapsedContent}>
                    <Tooltip content={toggleDrawerAriaLabel} relationship='description' appearance='inverted' withArrow positioning='above'>
                        <Button
                            appearance='subtle'
                            icon={isDrawerExpanded ? <ChevronDoubleRightRegular /> : <ChevronDoubleLeftRegular />}
                            aria-label={toggleDrawerAriaLabel}
                            className={styles.collapsedButton}
                            data-testid='expand-button'
                            onClick={handleToggleDrawer}
                        />
                    </Tooltip>
                    <span className={styles.formsText}>{t('formsAndReports')}</span>
                </div>
            )}
        </SidebarComponent>
    );
};

export default FormTOCSidebar;
